import { Link } from 'react-router-dom';
import { MapPin, Clock, Phone, ArrowRight } from 'lucide-react';

export default function ServiceArea() {
  const mainAreas = [
    { name: 'Vaughan', description: 'Including Woodbridge, Maple, Kleinburg, and Concord' },
    { name: 'Richmond Hill', description: 'Full coverage including Oak Ridges and surrounding areas' },
    { name: 'Markham', description: 'Including Unionville, Thornhill, and Milliken' },
    { name: 'Aurora', description: 'Complete service coverage for all residential areas' },
    { name: 'Newmarket', description: 'Full service to all neighborhoods' },
    { name: 'King City', description: 'Including King Township and rural properties' }
  ];

  const allNeighborhoods = [
    'Vaughan', 'Richmond Hill', 'Markham', 'Thornhill', 'Aurora', 'Newmarket',
    'King City', 'Maple', 'Woodbridge', 'Kleinburg', 'Concord', 'Oak Ridges',
    'Unionville', 'Milliken', 'North Toronto (North of Steeles)', 'Nobleton',
    'Schomberg', 'Stouffville (West)', 'East Gwillimbury', 'Holland Landing'
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Service Area</h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            Proudly serving York Region and North Toronto with fast, reliable HVAC service since 2018.
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-[var(--color-text-dark)]">
                York Region & North Toronto Coverage
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                We provide comprehensive HVAC services throughout York Region and the northern areas of Toronto. 
                Our technicians are strategically located to ensure rapid response times, with most service calls 
                completed same-day or next-day.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--color-slate-blue)] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[var(--color-text-dark)]">Response Times:</strong>
                    <p className="text-gray-600">Emergency same-day • Regular service within 24-48 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[var(--color-slate-blue)] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[var(--color-text-dark)]">Coverage Radius:</strong>
                    <p className="text-gray-600">Approximately 40km from central Vaughan</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={20} className="text-[var(--color-slate-blue)] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[var(--color-text-dark)]">Not sure if we service your area?</strong>
                    <p className="text-gray-600">Call (905) 555-0142 and we'll confirm immediately</p>
                  </div>
                </div>
              </div>

              <div className="bg-[var(--color-neutral-bg)] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                  No Travel Charges Within Service Area
                </h3>
                <p className="text-gray-700">
                  Unlike some competitors, we don't add travel fees for any location within our standard 
                  service area. The price we quote is the price you pay.
                </p>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-gray-200 rounded-lg min-h-[500px] flex flex-col items-center justify-center p-8">
              <MapPin size={64} className="text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">Interactive Service Area Map</p>
              <p className="text-gray-400 text-center">
                Shows York Region municipalities with service coverage overlay
              </p>
              <div className="mt-8 w-full max-w-sm">
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 bg-[var(--color-slate-blue)] rounded"></div>
                    <span className="text-sm text-gray-700">Primary Service Area</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[var(--color-warm-amber)] rounded"></div>
                    <span className="text-sm text-gray-700">Extended Service (may have conditions)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Primary Service Areas
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start gap-3">
                  <MapPin size={24} className="text-[var(--color-slate-blue)] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[var(--color-text-dark)]">
                      {area.name}
                    </h3>
                    <p className="text-gray-600">{area.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Neighborhoods */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            All Service Neighborhoods
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allNeighborhoods.map((neighborhood, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin size={16} className="text-[var(--color-warm-amber)] flex-shrink-0" />
                  <span className="text-gray-700">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Spotlight */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Neighborhood Expertise
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Older Homes (Pre-1980)
              </h3>
              <p className="text-gray-700 mb-3">
                Many homes in established neighborhoods like Thornhill and older parts of Richmond Hill 
                have unique HVAC challenges. We're experienced with:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>• Retrofitting high-efficiency systems to older ductwork</li>
                <li>• Dealing with asbestos-wrapped ducts safely</li>
                <li>• Right-sizing replacements for homes with poor insulation</li>
                <li>• Converting from oil to gas heating</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                New Construction (Post-2000)
              </h3>
              <p className="text-gray-700 mb-3">
                Newer developments in Vaughan, North Markham, and Aurora often have modern, 
                high-efficiency requirements. We specialize in:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li>• HRV/ERV installation and service</li>
                <li>• Two-stage and variable-speed equipment</li>
                <li>• Smart thermostat integration</li>
                <li>• Maintaining builder warranties</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Out of Area Notice */}
      <section className="py-12 bg-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-2 border-gray-300 rounded-lg p-8 max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4 text-[var(--color-text-dark)]">
              Outside Our Service Area?
            </h3>
            <p className="text-gray-700 mb-6">
              While we'd love to help everyone, we limit our service area to ensure fast response times 
              and quality service. If you're outside our coverage zone, we're happy to recommend trusted 
              colleagues who service your area.
            </p>
            <a 
              href="tel:9055550142" 
              className="inline-flex items-center justify-center gap-2 bg-[var(--color-slate-blue)] text-white px-6 py-3 rounded font-semibold hover:bg-opacity-90 transition-colors"
            >
              <Phone size={20} />
              Call for Referral: (905) 555-0142
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Need HVAC Service in York Region?
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Fast, reliable service from licensed technicians. Same-day emergency response available.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
          >
            Book Your Service Call
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}